import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const STATUS_STYLE = { approved: 'bg-green-100 text-green-800', rejected: 'bg-rose-100 text-rose-700', pending: 'bg-amber-100 text-amber-800', withdrawn: 'bg-slate-100 text-slate-500' };
const fmt = (n) => Number(n || 0).toLocaleString();

export default function Hr() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ['global_admin', 'school_admin', 'principal'].includes(user?.role);
  const { data: balance } = useQuery({ queryKey: ['hr-balance'], queryFn: () => api.get('/hr/leave-balance').then((r) => r.data) });
  const { data: leaves } = useQuery({ queryKey: ['hr-leaves'], queryFn: () => api.get('/hr/leaves').then((r) => r.data) });
  const { data: payslips } = useQuery({ queryKey: ['hr-payslips'], queryFn: () => api.get('/hr/my-payslips').then((r) => r.data) });
  const { data: salaries } = useQuery({ queryKey: ['hr-salaries'], queryFn: () => api.get('/hr/salaries').then((r) => r.data), enabled: isAdmin });
  const { data: staff } = useQuery({ queryKey: ['hr-staff'], queryFn: () => api.get('/users').then((r) => r.data), enabled: isAdmin });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const { data: payroll } = useQuery({ queryKey: ['hr-payroll', month], queryFn: () => api.get('/hr/payroll', { params: { month } }).then((r) => r.data), enabled: isAdmin });
  const [form, setForm] = useState({ type: 'casual', from_date: '', to_date: '', reason: '' });
  const inval = (k) => qc.invalidateQueries({ queryKey: [k] });

  const leaveMut = useMutation({
    mutationFn: () => api.post('/hr/leaves', form),
    onSuccess: () => { toast.success('Leave requested - your admin has been notified'); setForm({ ...form, from_date: '', to_date: '', reason: '' }); inval('hr-leaves'); inval('hr-balance'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const decideMut = useMutation({
    mutationFn: ({ id, approve, note }) => api.post(`/hr/leaves/${id}/decide`, { approve, note }),
    onSuccess: (r) => { toast.success(`Leave ${r.data.status}`); inval('hr-leaves'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const withdrawMut = useMutation({
    mutationFn: (id) => api.post(`/hr/leaves/${id}/withdraw`),
    onSuccess: () => { toast.success('Leave request withdrawn'); inval('hr-leaves'); inval('hr-balance'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const salaryMut = useMutation({
    mutationFn: (body) => api.post('/hr/salary', body),
    onSuccess: (r) => { toast.success(`Salary saved - net monthly ${fmt(r.data.net_monthly)}`); inval('hr-salaries'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const runMut = useMutation({
    mutationFn: () => api.post('/hr/payroll/run', { month }),
    onSuccess: (r) => { toast.success(`${r.data.payslips_created} payslip(s) generated for ${r.data.month} (${r.data.skipped} skipped)`); inval('hr-payroll'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const paidMut = useMutation({
    mutationFn: () => api.post(`/hr/payroll/${month}/mark-paid`),
    onSuccess: (r) => { toast.success(`${r.data.marked_paid} payslip(s) marked paid - staff notified`); inval('hr-payroll'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const setSalary = (u) => {
    const cur = (salaries || []).find((s) => s.user_id === u.id) || {};
    const basic = parseFloat(window.prompt(`Basic monthly salary for ${u.name}:`, cur.basic || ''));
    if (!basic && basic !== 0) return;
    const hra = parseFloat(window.prompt('HRA:', cur.hra ?? 0)) || 0;
    const allowances = parseFloat(window.prompt('Other allowances:', cur.allowances ?? 0)) || 0;
    const deductions = parseFloat(window.prompt('Deductions (PF, tax...):', cur.deductions ?? 0)) || 0;
    salaryMut.mutate({ user_id: u.id, basic, hra, allowances, deductions });
  };
  const pendingLeaves = (leaves || []).filter((l) => l.status === 'pending' && l.user_id !== user?.id);
  const removeSalaryMut = useMutation({
    mutationFn: (uid) => api.delete(`/hr/salary/${uid}`),
    onSuccess: () => { toast.success('Salary structure removed'); inval('hr-salaries'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const downloadSlip = async (p) => {
    try {
      const r = await api.get(`/hr/payslips/${p.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `payslip-${p.month}-${(p.name || '').replace(/ /g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip PDF downloaded');
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="hr-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">HR & Payroll</h1>
      <p className="text-sm text-slate-500 mb-6">Leave requests, approvals, salary structures & monthly payslips</p>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="leave-balance-panel">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">My leave balance ({new Date().getFullYear()})</p>
          {balance && Object.entries(balance).map(([lt, b]) => (
            <div key={lt} className="py-1.5 border-t border-slate-100 text-sm flex items-center gap-2" data-testid={`balance-${lt}`}>
              <span className="capitalize font-medium w-16">{lt}</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(b.available / b.quota) * 100}%` }} />
              </div>
              <span className="text-xs text-slate-500"><b className="text-slate-900">{b.available}</b>/{b.quota} left{b.pending ? ` · ${b.pending} pending` : ''}</span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4 lg:col-span-2" data-testid="request-leave-panel">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Request leave</p>
          <div className="grid sm:grid-cols-4 gap-2">
            <select data-testid="leave-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
              {Object.keys(balance || { casual: 1, sick: 1, earned: 1 }).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input data-testid="leave-from-input" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} />
            <Input data-testid="leave-to-input" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} />
            <Button data-testid="leave-submit" className="bg-slate-900 hover:bg-slate-800" disabled={!form.from_date || !form.to_date || leaveMut.isPending} onClick={() => leaveMut.mutate()}>Request</Button>
          </div>
          <Input data-testid="leave-reason-input" className="mt-2" placeholder="Reason (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </div>
      </div>

      {isAdmin && pendingLeaves.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-md p-4 mb-6" data-testid="leave-approvals-panel">
          <p className="font-mono-label text-[10px] uppercase text-amber-700 mb-2">Leave requests awaiting your decision</p>
          {pendingLeaves.map((l) => (
            <div key={l.id} className="py-2 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap" data-testid={`leave-pending-${l.id}`}>
              <span className="font-medium">{l.user_name}</span>
              <span className="text-xs text-slate-400">{l.user_role?.replace(/_/g, ' ')}</span>
              <span className="font-mono-label text-[10px] uppercase bg-slate-100 rounded px-1.5 py-0.5">{l.days}d {l.type}</span>
              <span className="text-xs text-slate-500">{l.from_date} - {l.to_date}{l.reason ? ` · “${l.reason}”` : ''}</span>
              <span className="ms-auto flex gap-2">
                <button data-testid={`leave-approve-${l.id}`} onClick={() => decideMut.mutate({ id: l.id, approve: true, note: '' })} className="text-xs bg-green-600 text-white rounded px-2.5 py-1 hover:bg-green-500">Approve</button>
                <button data-testid={`leave-reject-${l.id}`} onClick={() => { const note = window.prompt('Reason for rejection:') || ''; decideMut.mutate({ id: l.id, approve: false, note }); }} className="text-xs border border-rose-200 text-rose-600 rounded px-2.5 py-1 hover:bg-rose-50">Reject</button>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6" data-testid="leaves-list">
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{isAdmin ? 'All leave requests' : 'My leave requests'}</p>
        {(leaves || []).slice(0, 30).map((l) => (
          <div key={l.id} className="py-1.5 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap">
            {isAdmin && <span className="font-medium">{l.user_name}</span>}
            <span className="text-xs">{l.days}d <span className="capitalize">{l.type}</span> · {l.from_date} - {l.to_date}</span>
            <span className="ms-auto flex items-center gap-2">
              {l.status === 'pending' && l.user_id === user?.id && (
                <button data-testid={`leave-withdraw-${l.id}`} onClick={() => withdrawMut.mutate(l.id)}
                  className="text-xs border border-slate-200 rounded px-2 py-0.5 hover:border-rose-400 hover:text-rose-600">Withdraw</button>
              )}
              <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${STATUS_STYLE[l.status] || 'bg-slate-100 text-slate-500'}`} data-testid={`leave-status-${l.id}`}>{l.status}</span>
            </span>
          </div>
        ))}
        {(leaves || []).length === 0 && <p className="text-sm text-slate-400 py-2">No leave requests yet.</p>}
      </div>

      {isAdmin && (
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="salaries-panel">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Salary structures</p>
            {(staff || []).filter((u) => !['student', 'guardian', 'global_admin'].includes(u.role) && u.status === 'active').map((u) => {
              const s = (salaries || []).find((x) => x.user_id === u.id);
              return (
                <div key={u.id} className="py-1.5 border-t border-slate-100 text-sm flex items-center gap-2" data-testid={`salary-row-${u.id}`}>
                  <span className="font-medium truncate">{u.name}</span>
                  <span className="text-xs text-slate-400">{u.role?.replace(/_/g, ' ')}</span>
                  <span className="ms-auto text-xs">{s ? <b>net {fmt(s.basic + s.hra + s.allowances - s.deductions)}/mo</b> : <span className="text-slate-400">not set</span>}</span>
                  <button data-testid={`salary-set-${u.id}`} onClick={() => setSalary(u)} className="text-xs underline text-orange-700">{s ? 'edit' : 'set'}</button>
                  {s && <button data-testid={`salary-remove-${u.id}`} onClick={() => window.confirm(`Remove salary structure for ${u.name}?`) && removeSalaryMut.mutate(u.id)} className="text-xs underline text-slate-300 hover:text-rose-600">remove</button>}
                </div>
              );
            })}
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="payroll-panel">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Payroll run</p>
            <div className="flex gap-2 items-center mb-3">
              <Input data-testid="payroll-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="max-w-[160px]" />
              <Button data-testid="payroll-run-button" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={runMut.isPending} onClick={() => runMut.mutate()}>Run payroll</Button>
              {(payroll || []).some((p) => p.status === 'generated') && (
                <Button data-testid="payroll-paid-button" size="sm" variant="outline" disabled={paidMut.isPending} onClick={() => paidMut.mutate()}>Mark month paid</Button>
              )}
            </div>
            {(payroll || []).map((p) => (
              <div key={p.id} className="py-1.5 border-t border-slate-100 text-sm flex items-center gap-2" data-testid={`payslip-row-${p.id}`}>
                <span className="font-medium truncate">{p.name}</span>
                <span className="text-xs text-slate-400">{p.month}</span>
                <span className="ms-auto font-semibold">net {fmt(p.net)}</span>
                <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${p.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{p.status}</span>
                <button data-testid={`payslip-pdf-${p.id}`} onClick={() => downloadSlip(p)} className="text-xs underline text-orange-700">PDF</button>
              </div>
            ))}
            {(payroll || []).length === 0 && <p className="text-sm text-slate-400">No payslips for {month} - set salaries then run payroll.</p>}
          </div>
        </div>
      )}

      {(payslips || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="my-payslips-panel">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">My payslips</p>
          {payslips.map((p) => (
            <div key={p.id} className="py-1.5 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap" data-testid={`my-payslip-${p.month}`}>
              <span className="font-mono-label text-xs">{p.month}</span>
              <span className="text-xs text-slate-500">basic {fmt(p.basic)} + hra {fmt(p.hra)} + allow {fmt(p.allowances)} − ded {fmt(p.deductions)}</span>
              <span className="ms-auto font-display font-bold">net {fmt(p.net)}</span>
              <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${p.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{p.status}</span>
              <button data-testid={`my-payslip-pdf-${p.month}`} onClick={() => downloadSlip(p)} className="text-xs bg-slate-900 text-white rounded px-2 py-1 hover:bg-slate-800">Download PDF</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
